import { createApp, defineAsyncComponent } from 'vue';
import { createPinia } from 'pinia';
import { piniaResetPlugin } from './plugins/piniaReset';
import App from './App.vue';
import router from './router';
import vuetify from './plugins/vuetify';
import '@/scss/style.scss';
import PerfectScrollbar from 'vue3-perfect-scrollbar';
// `app.use(VueTablerIcons)` enregistrait les 4213 icônes Tabler globalement
// (~3,34 Mo dans le bundle). On n'importe et n'enregistre que celles réellement
// utilisées en template. Nouvelle icône dans un template ? L'ajouter ici.
import {
  ArrowDownRightIcon,
  ArrowUpLeftIcon,
  ArticleIcon,
  BasketIcon,
  BellIcon,
  BluetoothIcon,
  BuildingArchIcon,
  ChecksIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleDotIcon,
  CircleIcon,
  CircleXIcon,
  ClockHour4Icon,
  ClockIcon,
  CreditCardIcon,
  DeviceLaptopIcon,
  DotsIcon,
  DotsVerticalIcon,
  EyeIcon,
  EyeOffIcon,
  FileTextIcon,
  GiftIcon,
  GridDotsIcon,
  HeartIcon,
  HelpIcon,
  InboxIcon,
  InfoCircleIcon,
  LockIcon,
  MailIcon,
  MapPinIcon,
  Menu2Icon,
  Message2Icon,
  MessageIcon,
  MoodSmileIcon,
  PackageIcon,
  PhoneIcon,
  PlayerPlayIcon,
  PlayerSkipBackIcon,
  PlayerSkipForwardIcon,
  SendIcon,
  StarIcon,
  TrashIcon,
  UserCircleIcon,
  UserIcon,
  Volume2Icon,
  VolumeIcon,
  WifiIcon,
  XIcon,
} from 'vue-tabler-icons';
import 'vue3-carousel/dist/carousel.css';
import { sentryService } from '@/services/sentry.service';

//ScrollTop
import VueScrollTo from 'vue-scrollto';

//LightBox
import VueEasyLightbox from 'vue-easy-lightbox';

import { createHead } from "@vueuse/head";
import { createGtm, useGtm } from "@gtm-support/vue-gtm";
import { hasAnalyticsConsent } from "@/stores/consentStore";
import i18n from "./i18n";
import tianjiPlugin, { tianjiService } from "./services/tianji.service";

import "./css/landing-variables.css"; // Landing page design system - must be global for :root variables
import "./css/reservation.css"; // Reservation page styles
import "./css/leaflet-popup.css"; // Leaflet popup styles for fishing spots
import "@mdi/font/css/materialdesignicons.css";
import "leaflet/dist/leaflet.css";

import AOS from "aos";
import "aos/dist/aos.css";

import Maska from 'maska';

// Diagnostic utilities are intentionally excluded from the production bundle.
// They are loaded lazily only in development (see the setTimeout block below).

// Création de l'application
const app = createApp(App);

const head = createHead();
app.use(head);

// Configuration des plugins
const pinia = createPinia();
pinia.use(piniaResetPlugin);
app.use(pinia);
app.use(router);
app.use(PerfectScrollbar);
// Enregistrement global restreint aux icônes Tabler utilisées en template.
const usedTablerIcons = {
  ArrowDownRightIcon,
  ArrowUpLeftIcon,
  ArticleIcon,
  BasketIcon,
  BellIcon,
  BluetoothIcon,
  BuildingArchIcon,
  ChecksIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleDotIcon,
  CircleIcon,
  CircleXIcon,
  ClockHour4Icon,
  ClockIcon,
  CreditCardIcon,
  DeviceLaptopIcon,
  DotsIcon,
  DotsVerticalIcon,
  EyeIcon,
  EyeOffIcon,
  FileTextIcon,
  GiftIcon,
  GridDotsIcon,
  HeartIcon,
  HelpIcon,
  InboxIcon,
  InfoCircleIcon,
  LockIcon,
  MailIcon,
  MapPinIcon,
  Menu2Icon,
  Message2Icon,
  MessageIcon,
  MoodSmileIcon,
  PackageIcon,
  PhoneIcon,
  PlayerPlayIcon,
  PlayerSkipBackIcon,
  PlayerSkipForwardIcon,
  SendIcon,
  StarIcon,
  TrashIcon,
  UserCircleIcon,
  UserIcon,
  Volume2Icon,
  VolumeIcon,
  WifiIcon,
  XIcon,
};
for (const [name, comp] of Object.entries(usedTablerIcons)) {
  app.component(name, comp);
}
app.use(i18n);
app.use(Maska);
// apexcharts (~500 Ko) n'est chargé que lorsqu'un composant `<apexchart>` est
// réellement rendu (dashboards), pas dans l'entrée initiale. Le nom `apexchart`
// (minuscule) est imposé par les usages `<apexchart>` en template.
// eslint-disable-next-line vue/component-definition-name-casing
app.component('apexchart', defineAsyncComponent(() => import('vue3-apexcharts')));
app.use(VueEasyLightbox);
app.use(vuetify);
app.use(VueScrollTo, {
    duration: 1000,
    easing: "ease",
});

// Configuration Tianji Analytics
app.use(tianjiPlugin);

// Configuration GTM (Google Tag Manager) si activé
// RGPD : contrairement à PostHog (seul service correctement gaté jusqu'ici),
// GTM chargeait son conteneur — et posait ses cookies — dès le boot de l'app,
// avant toute réponse au bandeau de consentement. `enabled: false` empêche
// `loadScript` de s'exécuter tant que le consentement n'est pas acquis ; le
// watcher plus bas (à côté de celui de PostHog) l'active dès qu'il l'est.
if (import.meta.env.VITE_APP_GTM_ENABLED === 'true') {
  app.use(
    createGtm({
      id: import.meta.env.VITE_APP_GTM_KEY,
      debug: false,
      vueRouter: router,
      enabled: hasAnalyticsConsent(),
    }),
  );
}

// Recover from stale JS chunks caused by a new deployment while the PWA service worker
// still serves the old index.html (whose chunk hashes no longer exist on the server).
// On detection: clear all SW caches once per session, then force a hard reload.
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const error = event.reason;
  const isChunkLoadError =
    error instanceof TypeError &&
    /dynamically imported module|Failed to fetch dynamically|Importing a module script failed/i.test(
      error.message,
    );
  if (!isChunkLoadError) return;

  // Suppress Sentry (and browser console) reporting — this is a handled recovery.
  event.preventDefault();

  const RELOAD_FLAG = 'chunk_stale_reload';
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return;
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    // sessionStorage may be unavailable (private mode); allow reload anyway
  }

  const doReload = () => window.location.reload();
  if (typeof caches !== 'undefined') {
    caches
      .keys()
      .then((names) => Promise.all(names.map((n) => caches.delete(n))))
      .then(doReload)
      .catch(doReload);
  } else {
    doReload();
  }
});

// Initialize Sentry before mounting — must be called after all app.use() registrations
// so Vue integrations (error handler, router) are correctly wired.
sentryService.init(app);

// Montage de l'application (doit être fait AVANT les initialisations qui nécessitent le DOM)
app.mount("#app");

// Initialisation de AOS après montage
AOS.init({
  once: true,
  disable: "phone",
  duration: 700,
  easing: "ease-out-cubic",
});

// Initialize PostHog if already consented (returning visitor).
// Must run after app.mount() so Pinia stores are available.
void Promise.all([
  import("@/services/posthog.service"),
  import("@/stores/consentStore"),
  import("vue"),
]).then(([{ posthogService }, { useConsentStore }, { watch }]) => {
  const consentStore = useConsentStore()
  if (consentStore.analytics) {
    void posthogService.init()
  }
  watch(
    () => consentStore.analytics,
    (consented) => {
      if (consented) {
        void posthogService.init()
      } else {
        posthogService.shutdown()
      }
      // GTM : bascule live sans recharger la page (accepter/refuser dans le
      // bandeau). `enable(false)` ne retire pas le conteneur déjà chargé,
      // mais empêche l'envoi de nouveaux événements — cohérent avec le
      // comportement "opt-out" attendu d'un refus de consentement.
      useGtm()?.enable(consented)
    },
  )
})

// Wire Sentry user context whenever auth state changes.
// Deferred so Pinia is active and mount performance is unaffected.
void Promise.all([import("@/stores/authStore"), import("vue")]).then(
  ([{ useAuthStore }, { watch }]) => {
    const authStore = useAuthStore();
    // Set initial context if already authenticated at load time
    if (authStore.user) {
      sentryService.setUser({
        id: authStore.user.id ?? "unknown",
        email: authStore.user.email ?? "",
        orgSlug: authStore.user.organization_slug ?? "",
      });
    }
    // Keep context in sync with auth state changes
    watch(
      () => authStore.user,
      (user) => {
        if (user) {
          sentryService.setUser({
            id: user.id ?? "unknown",
            email: user.email ?? "",
            orgSlug: user.organization_slug ?? "",
          });
        } else {
          sentryService.setUser(null);
        }
      },
    );
  },
);

// Import des services de tracking
import { funnelService } from "./services/funnel.service";
import { funnelEventsService } from "./services/funnel-events.service";
import { funnelAnalyticsService } from "./services/funnel-analytics.service";
import { getFunnelMappingForRoute } from "./utils/route-funnel.mapping";

// Initialisation de Tianji et des funnels après montage.
// Le script tracker.js lui-même n'est injecté (vite.config.js) que si le
// consentement analytics est déjà acquis ; `init()` attend son apparition et
// abandonne proprement sinon (cf. waitForTianjiScript). Si le consentement est
// donné en cours de session, l'évènement déclenché par consentStore relance
// l'initialisation une fois le script chargé.
window.addEventListener("mylimni:analytics-consent-granted", () => {
  tianjiService.init();
});

setTimeout(() => {
  tianjiService.init();

  // Development-only: expose service globals and diagnostic utilities.
  // These are never bundled or executed in production thanks to Vite's DEV-flag
  // dead-code elimination on static import.meta.env.DEV checks.
  if (import.meta.env.DEV) {
    if (typeof window !== "undefined") {
      (window as unknown as Record<string, unknown>).funnelService = funnelService;
      (window as unknown as Record<string, unknown>).funnelEventsService = funnelEventsService;
      (window as unknown as Record<string, unknown>).funnelAnalyticsService = funnelAnalyticsService;
      (window as unknown as Record<string, unknown>).getFunnelMappingForRoute = getFunnelMappingForRoute;
    }
    import("./utils/tianji-test").then(({ exposeTestFunctions }) => exposeTestFunctions());
    import("./utils/mistral-diagnostic");
    import("./utils/ai-diagnostic");
    import("./utils/create-test-docx");
    import("./utils/auth-diagnostic");
  }
}, 100);
